import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Share as ShareIcon,
  PeopleOutline as PeopleIcon,
} from "@mui/icons-material";
import { DataGrid, type GridColDef, GridToolbar } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import {
  getReferralList,
  getReferralSummary,
  sendReferralInvite,
} from "../../apis/referralApi";
import type {
  ReferralConflictReason,
  ReferralListItem,
  ReferralSummary,
} from "../../schemas/referral";

/** Outcomes that mean the invite does not apply, rather than something failing. */
const CONFLICT_REASONS: ReferralConflictReason[] = [
  "already_referred_by_you",
  "already_referred_by_other",
  "invite_already_sent_by_you",
  "invite_sent_by_other",
  "invite_forfeited",
  "already_active_member",
  "not_new_member",
  "self_referral",
];

type ChipColor = "success" | "warning" | "info" | "default" | "error";

const STATUS_COLORS: Record<string, ChipColor> = {
  active: "success",
  cancelled: "warning",
  paused: "default",
  expired: "error",
};

// "Expired" reads as a system event rather than something the member did, and
// "On hold" covers both a pause and a missed payment without accusing anyone.
const STATUS_LABELS: Record<string, string> = {
  expired: "Forfeited",
  paused: "On hold",
};

const ReferralsSection: React.FC = () => {
  const [summary, setSummary] = useState<ReferralSummary | null>(null);
  const [referrals, setReferrals] = useState<ReferralListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  const fetchData = useCallback(async (options?: { showLoading?: boolean }) => {
    const showLoading = options?.showLoading ?? true;
    try {
      if (showLoading) setLoading(true);
      setError(null);
      const [summaryData, listData] = await Promise.all([
        getReferralSummary(),
        getReferralList(),
      ]);
      setSummary(summaryData);
      setReferrals(listData);
    } catch (err) {
      console.error("Error loading referrals:", err);
      setError("Failed to load referral information. Please try again.");
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCopyLink = async () => {
    if (!summary?.referralLink) return;
    try {
      await navigator.clipboard.writeText(summary.referralLink);
      toast.success("Referral link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    try {
      setInviting(true);
      await sendReferralInvite(inviteEmail.trim());
      toast.success("Invitation sent");
      setInviteEmail("");
      await fetchData({ showLoading: false });
    } catch (err: any) {
      const message =
        err?.response?.data?.message || "Failed to send invitation";
      const reason = err?.response?.data?.reason as
        | ReferralConflictReason
        | undefined;
      // "This person is already taken" is an answer, not a failure.
      if (reason && CONFLICT_REASONS.includes(reason)) {
        toast.info(message);
      } else {
        toast.error(message);
      }
    } finally {
      setInviting(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 140,
      valueGetter: (_value, row) => row.name || "—",
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.2,
      minWidth: 180,
      valueGetter: (_value, row) => row.email || "—",
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const status = String(params.value);
        return (
          <Chip
            label={STATUS_LABELS[status] || status.charAt(0).toUpperCase() + status.slice(1)}
            color={STATUS_COLORS[status] || "info"}
            size="small"
            variant={status === "active" ? "filled" : "outlined"}
          />
        );
      },
    },
    {
      field: "rewardStatus",
      headerName: "Reward",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={String(params.value).charAt(0).toUpperCase() + String(params.value).slice(1)}
          size="small"
          variant="outlined"
          color={
            params.value === "active"
              ? "success"
              : params.value === "revoked"
                ? "default"
                : params.value === "paused"
                  ? "warning"
                  : "info"
          }
        />
      ),
    },
    {
      field: "discountContribution",
      headerName: "Discount",
      width: 110,
      valueGetter: (_value, row) =>
        row.discountContribution > 0 ? `${row.discountContribution}%` : "—",
    },
    {
      field: "activatedAt",
      headerName: "Activated",
      width: 130,
      valueGetter: (_value, row) =>
        row.activatedAt
          ? new Date(row.activatedAt).toLocaleDateString()
          : "—",
    },
    {
      field: "cancelledAt",
      headerName: "Cancelled",
      width: 130,
      valueGetter: (_value, row) =>
        row.cancelledAt
          ? new Date(row.cancelledAt).toLocaleDateString()
          : "—",
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom fontWeight={600}>
          Referrals
        </Typography>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => fetchData()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Referrals
      </Typography>

      {!summary?.isEligible &&
        (summary?.rewardsOnHoldForPayment ? (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Your membership payment did not go through. Your{" "}
            {summary.currentDiscountPercent}% referral discount still applies to the
            payment you owe, but you cannot send new invites until it clears.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 3 }}>
            Become an active member to refer friends and family and earn membership
            discounts.
          </Alert>
        ))}

      {summary && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                <Typography variant="body2" color="text.secondary">
                  Active referrals
                </Typography>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {summary.activeReferrals}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                <Typography variant="body2" color="text.secondary">
                  Current discount
                </Typography>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {summary.currentDiscountPercent}%
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                <Typography variant="body2" color="text.secondary">
                  Maximum cap
                </Typography>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {summary.maxDiscountCap}%
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {summary?.isEligible && (
        <>
          <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Your referral link
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Share this link with friends and family. They get{" "}
              {summary.welcomeDiscountForNewMembers}% off their first membership
              payment, then pay the regular price. You earn{" "}
              {summary.discountPerReferral}% off your membership for each active
              referral (up to {summary.maxDiscountCap}%).
            </Typography>
            <TextField
              fullWidth
              value={summary.referralLink}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopyLink} edge="end" aria-label="Copy referral link">
                      <CopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            <Button
              variant="contained"
              startIcon={<CopyIcon />}
              onClick={handleCopyLink}
            >
              Copy referral link
            </Button>
          </Paper>

          <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Invite by email
            </Typography>
            <Box
              component="form"
              onSubmit={handleInvite}
              sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}
            >
              <TextField
                label="Email address"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={inviting}
                sx={{ flex: 1, minWidth: 240 }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={inviting}
                startIcon={inviting ? <CircularProgress size={18} color="inherit" /> : <ShareIcon />}
                sx={{ height: 56 }}
              >
                {inviting ? "Sending..." : "Send invite"}
              </Button>
            </Box>
          </Paper>
        </>
      )}

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Referral list
        </Typography>

        {referrals.length === 0 ? (
          <Card variant="outlined" sx={{ textAlign: "center", py: 4 }}>
            <CardContent>
              <PeopleIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                No referrals yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {summary?.isEligible
                  ? "Share your referral link or invite someone by email to get started."
                  : "Once you are an active member, you can invite friends here."}
              </Typography>
              {summary?.isEligible && (
                <Button variant="outlined" startIcon={<CopyIcon />} onClick={handleCopyLink}>
                  Copy referral link
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ height: 420, width: "100%" }}>
            <DataGrid
              rows={referrals}
              columns={columns}
              getRowId={(row) => row.id}
              pageSizeOptions={[5, 10, 25]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              slots={{ toolbar: GridToolbar }}
              disableRowSelectionOnClick
              sx={{ border: 0 }}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ReferralsSection;
